import { DIAGRAM_MAX_ELEMENTS, roundCoordinate, type DiagramElement, type DiagramPitch, type DiagramTeam } from '@ukinfo/shared';
import { clamp, fieldDistance, nextPlayerLabel, snapToStep, toCoordinate, translateElement, type FieldPoint } from './diagramEdit';
import { PITCH_HEIGHT } from './PitchDiagram';

/**
 * Reine Rechenhilfen fuer Mehrfachauswahl, Einrasten, Ausrichten, Duplizieren und Reihen -
 * ohne React, damit sie sich einzeln pruefen lassen.
 *
 * Feldkoordinaten wie in `diagramEdit.ts`: x und y in Prozent des Spielfelds, Vielfache von 0,5.
 * Eine Auswahl ist eine Liste von Indizes in `elements`. Alle Funktionen liefern neue Listen
 * und lassen die Eingabe unveraendert; gibt es nichts zu aendern, kommt dieselbe Liste zurueck.
 */

export interface FieldBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function boxOfPoints(points: readonly FieldPoint[]): FieldBox | null {
  if (points.length === 0) return null;
  return {
    minX: Math.min(...points.map((p) => p.x)),
    minY: Math.min(...points.map((p) => p.y)),
    maxX: Math.max(...points.map((p) => p.x)),
    maxY: Math.max(...points.map((p) => p.y)),
  };
}

function unionBox(boxes: readonly FieldBox[]): FieldBox | null {
  if (boxes.length === 0) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

/** Auswahl bereinigen: nur vorhandene Indizes, ohne Doppelte, aufsteigend. */
export function normalizeSelection(indices: readonly number[], count: number): number[] {
  return [...new Set(indices)].filter((index) => Number.isInteger(index) && index >= 0 && index < count).sort((a, b) => a - b);
}

/* ------------------------------------------------------------------ Lage */

/**
 * Die Punkte, an denen ein Element fuer Rahmen und Einrasten "sitzt": der Punkt selbst,
 * bei Pfeilen und Masslinien Anfang und Ende, bei Zonen die Mitte.
 */
export function anchorPoints(element: DiagramElement): FieldPoint[] {
  switch (element.type) {
    case 'arrow':
    case 'measure':
      return [
        { x: element.from[0], y: element.from[1] },
        { x: element.to[0], y: element.to[1] },
      ];
    case 'zone':
      return [{ x: element.x + element.w / 2, y: element.y + element.h / 2 }];
    default:
      return [{ x: element.x, y: element.y }];
  }
}

/** Umriss eines Elements fuer das Ausrichten: Pfeil und Masslinie von Anfang bis Ende (ohne Kontrollpunkt), Zone ganz. */
export function elementBox(element: DiagramElement): FieldBox {
  if (element.type === 'zone') return { minX: element.x, minY: element.y, maxX: element.x + element.w, maxY: element.y + element.h };
  return boxOfPoints(anchorPoints(element))!;
}

/** Alles, was das Element belegt - bei gebogenen Pfeilen samt Kontrollpunkt, bei Knickpfeilen samt Knickpunkten. Davon haengt das Klemmen ins Feld ab. */
export function elementExtent(element: DiagramElement): FieldBox {
  if (element.type === 'arrow' && (element.via || element.points)) {
    const extra = [...(element.via ? [element.via] : []), ...(element.points ?? [])];
    return boxOfPoints([...anchorPoints(element), ...extra.map((point) => ({ x: point[0], y: point[1] }))])!;
  }
  return elementBox(element);
}

/** Umriss der Anker aller Elemente der Auswahl (Grundlage fuer Einrasten). */
export function anchorBox(elements: readonly DiagramElement[], indices: readonly number[]): FieldBox | null {
  return boxOfPoints(indices.flatMap((index) => (elements[index] ? anchorPoints(elements[index]!) : [])));
}

/* ------------------------------------------------------------------ Rahmen */

/**
 * Alle Elemente, die der Rahmen aus zwei gegenueberliegenden Ecken fasst: Punkte liegen im
 * Rahmen, bei Pfeilen beide Enden, bei Zonen die Mitte. Der Rand zaehlt mit.
 */
export function elementsInRect(elements: readonly DiagramElement[], a: FieldPoint, b: FieldPoint): number[] {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  const result: number[] = [];
  elements.forEach((element, index) => {
    if (anchorPoints(element).every((p) => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY)) result.push(index);
  });
  return result;
}

/* ------------------------------------------------------- Verschieben, Einrasten */

/** Verschiebung, die die ganze Gruppe im Feld haelt: die Form bleibt erhalten, nur die Strecke wird gekuerzt. */
export function clampGroupDelta(elements: readonly DiagramElement[], indices: readonly number[], dx: number, dy: number): { dx: number; dy: number } {
  const extent = unionBox(indices.flatMap((index) => (elements[index] ? [elementExtent(elements[index]!)] : [])));
  if (!extent) return { dx: 0, dy: 0 };
  return {
    dx: roundCoordinate(clamp(dx, -extent.minX, 100 - extent.maxX)),
    dy: roundCoordinate(clamp(dy, -extent.minY, 100 - extent.maxY)),
  };
}

/** Verschiebt die Elemente der Auswahl um dieselbe, schon geklemmte Strecke. */
function shiftGroup(elements: readonly DiagramElement[], indices: readonly number[], dx: number, dy: number): DiagramElement[] {
  if (dx === 0 && dy === 0) return elements as DiagramElement[];
  const selected = new Set(indices);
  return elements.map((element, index) => (selected.has(index) ? translateElement(element, dx, dy) : element));
}

/** Verschiebt die Gruppe um (dx, dy), ohne dass ein Teil das Feld verlaesst. */
export function translateGroup(elements: readonly DiagramElement[], indices: readonly number[], dx: number, dy: number): DiagramElement[] {
  const clamped = clampGroupDelta(elements, indices, dx, dy);
  return shiftGroup(elements, indices, clamped.dx, clamped.dy);
}

/** Hilfslinien: die x-Werte (senkrechte Linien) und y-Werte (waagrechte Linien), an denen gerade eingerastet ist. */
export interface SnapGuides {
  x: number[];
  y: number[];
}

export const NO_GUIDES: SnapGuides = { x: [], y: [] };

/** Bildschirmtoleranz des Einrastens in Pixeln. */
export const SNAP_PIXELS = 8;

/**
 * Toleranz in Feldkoordinaten: `pixels` Bildschirmpixel, mit dem Zoom umgerechnet. x zaehlt in
 * Prozent der Breite, y in Prozent der Hoehe - die Hoehe haengt vom Feld ab. Ohne messbaren
 * Kasten (`pixelsPerUnit` 0) gibt es keine Toleranz, also kein Einrasten.
 */
export function snapTolerance(pixelsPerUnit: number, pitch: DiagramPitch, pixels = SNAP_PIXELS): { x: number; y: number } {
  if (!(pixelsPerUnit > 0)) return { x: 0, y: 0 };
  const units = pixels / pixelsPerUnit;
  return { x: units, y: (units * 100) / PITCH_HEIGHT[pitch] };
}

interface SnapTargets {
  xs: number[];
  ys: number[];
}

/** Ziele zum Einrasten: die Anker aller nicht gezogenen Elemente und die Feldmitte. */
export function snapTargets(elements: readonly DiagramElement[], dragged: readonly number[]): SnapTargets {
  const moving = new Set(dragged);
  const xs = [50];
  const ys = [50];
  elements.forEach((element, index) => {
    if (moving.has(index)) return;
    for (const point of anchorPoints(element)) {
      xs.push(point.x);
      ys.push(point.y);
    }
  });
  return { xs: [...new Set(xs)], ys: [...new Set(ys)] };
}

/** Kante, Mitte und Kante der gezogenen Gruppe auf einer Achse. */
function edges(min: number, max: number): number[] {
  return [...new Set([min, (min + max) / 2, max])];
}

/**
 * Naechste Verschiebung auf einer Achse, bei der eine Kante der Gruppe genau auf ein Ziel faellt (sonst unveraendert);
 * `hit` sagt, ob es eines gab.
 */
function snapAxis(candidates: readonly number[], delta: number, targets: readonly number[], tolerance: number): { value: number; hit: boolean } {
  let best: number | null = null;
  for (const candidate of candidates) {
    for (const target of targets) {
      const diff = target - (candidate + delta);
      // Nur Ziele, die auf das Raster der Koordinaten (Vielfache von 0,5) fallen, lassen sich treffen.
      if (!Number.isInteger(diff * 2) || Math.abs(diff) > tolerance) continue;
      if (best === null || Math.abs(diff) < Math.abs(best)) best = diff;
    }
  }
  return best === null ? { value: delta, hit: false } : { value: delta + best, hit: true };
}

/** Rastet die Kante `min` der Gruppe, um `delta` verschoben, auf ein Raster der Weite `step`; liefert die neue Verschiebung. */
function gridAxis(min: number, delta: number, step: number): number {
  return snapToStep(min + delta, step) - min;
}

/**
 * Rastet die Verschiebung (dx, dy) der Gruppe `box` an den Zielen ein; wo kein Ziel in der Naehe liegt, rastet sie mit `grid`
 * (Weite des Rasters in Feldprozent) auf das Raster - das Einrasten an Objekten hat Vorrang vor dem Raster.
 */
export function snapDelta(
  box: FieldBox,
  dx: number,
  dy: number,
  targets: SnapTargets,
  tolerance: { x: number; y: number },
  grid: number | null = null,
): { dx: number; dy: number } {
  const x = snapAxis(edges(box.minX, box.maxX), dx, targets.xs, tolerance.x);
  const y = snapAxis(edges(box.minY, box.maxY), dy, targets.ys, tolerance.y);
  return {
    dx: roundCoordinate(!x.hit && grid ? gridAxis(box.minX, x.value, grid) : x.value),
    dy: roundCoordinate(!y.hit && grid ? gridAxis(box.minY, y.value, grid) : y.value),
  };
}

/** Die Ziele, auf denen eine Kante der um (dx, dy) verschobenen Gruppe genau liegt. */
function matchingGuides(box: FieldBox, dx: number, dy: number, targets: SnapTargets): SnapGuides {
  const hit = (candidates: readonly number[], delta: number, values: readonly number[]): number[] =>
    values.filter((value) => candidates.some((candidate) => candidate + delta === value)).sort((a, b) => a - b);
  return { x: hit(edges(box.minX, box.maxX), dx, targets.xs), y: hit(edges(box.minY, box.maxY), dy, targets.ys) };
}

export interface GroupMove {
  elements: DiagramElement[];
  /** Tatsaechlich angewandte Verschiebung (nach Einrasten und Klemmen). */
  dx: number;
  dy: number;
  guides: SnapGuides;
}

/**
 * Verschiebt die Auswahl um (dx, dy). Mit `snap` rastet sie zuerst an Kanten und Mitten anderer
 * Elemente und der Feldmitte ein (innerhalb der Toleranz), danach wird die Gruppe ins Feld geklemmt.
 * Die Hilfslinien nennen, wo die verschobene Gruppe wirklich ausgerichtet ist.
 */
export function moveGroup(
  elements: readonly DiagramElement[],
  indices: readonly number[],
  dx: number,
  dy: number,
  snap?: {
    /** Toleranz des Einrastens an anderen Elementen und der Feldmitte; ohne sie rastet nichts an Objekten ein. */
    tolerance?: { x: number; y: number };
    /** Weite des Rasters in Feldprozent; ohne sie gibt es kein Rasterrasten. */
    grid?: number | null;
  },
): GroupMove {
  const box = anchorBox(elements, indices);
  if (!snap || !box || (!snap.tolerance && !snap.grid)) {
    const clamped = clampGroupDelta(elements, indices, dx, dy);
    return { elements: shiftGroup(elements, indices, clamped.dx, clamped.dy), ...clamped, guides: NO_GUIDES };
  }
  const targets = snap.tolerance ? snapTargets(elements, indices) : { xs: [], ys: [] };
  const snapped = snapDelta(box, dx, dy, targets, snap.tolerance ?? { x: 0, y: 0 }, snap.grid ?? null);
  const clamped = clampGroupDelta(elements, indices, snapped.dx, snapped.dy);
  return { elements: shiftGroup(elements, indices, clamped.dx, clamped.dy), ...clamped, guides: matchingGuides(box, clamped.dx, clamped.dy, targets) };
}

/**
 * Rastet einen Endpunkt einer Masslinie ein: auf den naechsten Ankerpunkt eines anderen Elements
 * (Huetchen, Spieler, Pfeilende ...; Zonen zaehlen nicht), sonst waagrecht oder senkrecht zum
 * anderen Ende `other`, falls es bis auf die Toleranz fluchtet. `skip` sind Elemente, an denen nicht
 * eingerastet wird (die Masslinie selbst). Ohne Toleranz bleibt der Punkt, wie er ist.
 */
export function snapMeasurePoint(
  point: FieldPoint,
  other: FieldPoint | null,
  elements: readonly DiagramElement[],
  skip: readonly number[],
  tolerance: { x: number; y: number } | null,
  pitch: DiagramPitch,
): FieldPoint {
  if (!tolerance) return point;
  let best: FieldPoint | null = null;
  let bestDistance = Infinity;
  elements.forEach((element, index) => {
    if (skip.includes(index) || element.type === 'zone') return;
    for (const anchor of anchorPoints(element)) {
      if (Math.abs(anchor.x - point.x) > tolerance.x || Math.abs(anchor.y - point.y) > tolerance.y) continue;
      const gap = fieldDistance(anchor, point, pitch);
      if (gap < bestDistance) {
        best = anchor;
        bestDistance = gap;
      }
    }
  });
  if (best) return best;
  if (!other) return point;
  return {
    x: Math.abs(other.x - point.x) <= tolerance.x ? other.x : point.x,
    y: Math.abs(other.y - point.y) <= tolerance.y ? other.y : point.y,
  };
}

/* ------------------------------------------------------------------ Zentrieren */

/**
 * Zentriert die Auswahl als Gruppe in der Feldmitte: `x` waagrecht (Mitte des gemeinsamen Umrisses auf
 * x = 50), `y` senkrecht (auf y = 50). Alle Elemente wandern um dieselbe Strecke, Form und Abstände der Gruppe
 * bleiben; die Strecke wird so gekürzt, dass nichts das Feld verlässt. Bei einem einzelnen Element ist das
 * Ergebnis dasselbe wie bei `centerOnField`.
 */
export function centerGroupOnField(elements: readonly DiagramElement[], indices: readonly number[], axis: 'x' | 'y'): DiagramElement[] {
  const box = unionBox(indices.flatMap((index) => (elements[index] ? [elementBox(elements[index]!)] : [])));
  if (!box) return elements as DiagramElement[];
  const delta = axis === 'x' ? 50 - (box.minX + box.maxX) / 2 : 50 - (box.minY + box.maxY) / 2;
  const clamped = axis === 'x' ? clampGroupDelta(elements, indices, delta, 0) : clampGroupDelta(elements, indices, 0, delta);
  return shiftGroup(elements, indices, clamped.dx, clamped.dy);
}

/**
 * Zentriert die Auswahl auf der Mittellinie des Felds: Jedes Element der Auswahl wandert so, dass seine Mitte
 * auf der Mittellinie liegt (`x`: x = 50, `y`: y = 50) - mehrere Elemente stehen danach gemeinsam auf einer
 * Linie. Jedes Element wird einzeln ins Feld geklemmt. Die Gruppe als Ganzes rückt `centerGroupOnField`.
 */
export function centerOnField(elements: readonly DiagramElement[], indices: readonly number[], axis: 'x' | 'y'): DiagramElement[] {
  return centerEach(elements, new Set(indices), 50, axis);
}

/**
 * Zentriert die Auswahl an einem Bezugsobjekt: Jedes Element der Auswahl wandert so, dass seine Mitte
 * auf der Mitte des Bezugsobjekts liegt (`x`: gleiche Mitte waagrecht, `y`: senkrecht). Das Bezugsobjekt
 * selbst bleibt stehen - es darf Teil der Auswahl sein oder ein anderes Element. Jedes Element wird
 * einzeln ins Feld geklemmt.
 */
export function centerOnObject(elements: readonly DiagramElement[], indices: readonly number[], reference: number, axis: 'x' | 'y'): DiagramElement[] {
  const ref = elements[reference];
  if (!ref) return elements as DiagramElement[];
  const refBox = elementBox(ref);
  const target = axis === 'x' ? (refBox.minX + refBox.maxX) / 2 : (refBox.minY + refBox.maxY) / 2;
  return centerEach(elements, new Set(indices.filter((index) => index !== reference)), target, axis);
}

/** Rueckt die Mitte jedes Elements aus `moving` auf `target`; liefert dieselbe Liste, wenn sich nichts aendert. */
function centerEach(elements: readonly DiagramElement[], moving: ReadonlySet<number>, target: number, axis: 'x' | 'y'): DiagramElement[] {
  let changed = false;
  const next = elements.map((element, index) => {
    if (!moving.has(index)) return element;
    const box = elementBox(element);
    const own = axis === 'x' ? (box.minX + box.maxX) / 2 : (box.minY + box.maxY) / 2;
    const shift = roundCoordinate(target - own);
    if (shift === 0) return element;
    const moved = axis === 'x' ? translateElement(element, shift, 0) : translateElement(element, 0, shift);
    if (JSON.stringify(moved) !== JSON.stringify(element)) changed = true;
    return moved;
  });
  return changed ? next : (elements as DiagramElement[]);
}

/* ------------------------------------------------------------------ Mannschaft */

/**
 * Setzt alle Spieler der Auswahl auf die Mannschaft `team` (andere Elemente bleiben). Traegt ein
 * Spieler eine Nummer, die in der neuen Mannschaft schon vergeben ist, bekommt er die naechste freie.
 */
export function changeTeam(elements: readonly DiagramElement[], indices: readonly number[], team: DiagramTeam): DiagramElement[] {
  const next = [...elements];
  let changed = false;
  for (const index of [...new Set(indices)].sort((a, b) => a - b)) {
    const element = next[index];
    if (!element || element.type !== 'player' || element.team === team) continue;
    changed = true;
    let label = element.label;
    if (label && /^\d+$/.test(label)) {
      const taken = next.some((other, i) => i !== index && other.type === 'player' && other.team === team && other.label === label);
      if (taken) label = nextPlayerLabel(next.filter((_, i) => i !== index), team);
    }
    next[index] = label === undefined ? { ...element, team } : { ...element, team, label };
  }
  return changed ? next : (elements as DiagramElement[]);
}

/* ------------------------------------------------------------------ Ausrichten */

export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

/**
 * Richtet die Auswahl aus: bündig links/rechts/oben/unten oder mittig. Massgeblich ist der
 * gemeinsame Umriss der Auswahl; jedes Element wird nur auf der betroffenen Achse verschoben.
 */
export function alignElements(elements: readonly DiagramElement[], indices: readonly number[], mode: AlignMode): DiagramElement[] {
  const picked = indices.filter((index) => elements[index]);
  if (picked.length < 2) return elements as DiagramElement[];
  const boxes = new Map(picked.map((index) => [index, elementBox(elements[index]!)] as const));
  const all = unionBox([...boxes.values()])!;
  const horizontal = mode === 'left' || mode === 'centerX' || mode === 'right';
  const target = {
    left: all.minX,
    centerX: (all.minX + all.maxX) / 2,
    right: all.maxX,
    top: all.minY,
    centerY: (all.minY + all.maxY) / 2,
    bottom: all.maxY,
  }[mode];
  let changed = false;
  const next = elements.map((element, index) => {
    const box = boxes.get(index);
    if (!box) return element;
    const own = {
      left: box.minX,
      centerX: (box.minX + box.maxX) / 2,
      right: box.maxX,
      top: box.minY,
      centerY: (box.minY + box.maxY) / 2,
      bottom: box.maxY,
    }[mode];
    const shift = roundCoordinate(target - own);
    if (shift === 0) return element;
    const moved = horizontal ? translateElement(element, shift, 0) : translateElement(element, 0, shift);
    if (JSON.stringify(moved) !== JSON.stringify(element)) changed = true;
    return moved;
  });
  return changed ? next : (elements as DiagramElement[]);
}

/**
 * Verteilt die Auswahl gleichmaessig zwischen den beiden aeussersten Elementen: Die Mitten
 * liegen danach in gleichen Abstaenden (auf halbe Einheiten gerundet), die beiden aeussersten bleiben stehen.
 * Braucht mindestens drei Elemente.
 */
export function distributeElements(elements: readonly DiagramElement[], indices: readonly number[], axis: 'x' | 'y'): DiagramElement[] {
  const picked = indices.filter((index) => elements[index]);
  if (picked.length < 3) return elements as DiagramElement[];
  const center = (index: number): number => {
    const box = elementBox(elements[index]!);
    return axis === 'x' ? (box.minX + box.maxX) / 2 : (box.minY + box.maxY) / 2;
  };
  const sorted = [...picked].sort((a, b) => center(a) - center(b) || a - b);
  const first = center(sorted[0]!);
  const last = center(sorted[sorted.length - 1]!);
  const shifts = new Map<number, number>();
  sorted.forEach((index, position) => {
    const target = first + ((last - first) * position) / (sorted.length - 1);
    shifts.set(index, roundCoordinate(target - center(index)));
  });
  let changed = false;
  const next = elements.map((element, index) => {
    const shift = shifts.get(index);
    if (!shift) return element;
    const moved = axis === 'x' ? translateElement(element, shift, 0) : translateElement(element, 0, shift);
    if (JSON.stringify(moved) !== JSON.stringify(element)) changed = true;
    return moved;
  });
  return changed ? next : (elements as DiagramElement[]);
}

/* ------------------------------------------------------------------ Duplizieren */

/** Versatz einer Kopie in Zeichnungseinheiten. */
export const DUPLICATE_OFFSET = 5;

/**
 * Versatz fuer Kopien in Feldprozent: nach rechts unten, auf einer Achse nach links/oben,
 * wenn rechts/unten kein Platz ist; passt keins von beidem, bleibt die Kopie auf dieser Achse darueber.
 */
export function duplicateOffset(elements: readonly DiagramElement[], indices: readonly number[], pitch: DiagramPitch): FieldPoint {
  const extent = unionBox(indices.flatMap((index) => (elements[index] ? [elementExtent(elements[index]!)] : [])));
  if (!extent) return { x: 0, y: 0 };
  const stepX = DUPLICATE_OFFSET;
  const stepY = Math.max(2, Math.round((DUPLICATE_OFFSET * 100) / PITCH_HEIGHT[pitch]));
  const pick = (min: number, max: number, step: number): number => (max + step <= 100 ? step : min - step >= 0 ? -step : 0);
  return { x: pick(extent.minX, extent.maxX, stepX), y: pick(extent.minY, extent.maxY, stepY) };
}

/**
 * Spielernummer fuer eine Kopie: Rein numerische Beschriftungen bekommen die naechste freie
 * Nummer der Mannschaft; "TW" und andere Texte (und fehlende Beschriftungen) bleiben, wie sie sind.
 */
export function renumbered(element: DiagramElement, pool: readonly DiagramElement[]): DiagramElement {
  if (element.type !== 'player' || !element.label || !/^\d+$/.test(element.label)) return element;
  return { ...element, label: nextPlayerLabel(pool, element.team) };
}

/**
 * Kopien der Auswahl, um `offset` versetzt (die Gruppe bleibt dabei im Feld). Spieler werden
 * je Mannschaft fortlaufend neu nummeriert. Die Reihenfolge entspricht der der Auswahl.
 */
export function copyElements(elements: readonly DiagramElement[], indices: readonly number[], offset: FieldPoint): DiagramElement[] {
  const sorted = [...indices].filter((index) => elements[index]).sort((a, b) => a - b);
  const clamped = clampGroupDelta(elements, sorted, offset.x, offset.y);
  const copies: DiagramElement[] = [];
  for (const index of sorted) {
    const moved = translateElement(elements[index]!, clamped.dx, clamped.dy);
    copies.push(renumbered(moved, [...elements, ...copies]));
  }
  return copies;
}

/* ------------------------------------------------------------------ Reihe */

export const ROW_MIN_COUNT = 2;
export const ROW_MAX_COUNT = 20;
/** Kleinster und groesster Abstand in Feldeinheiten (Prozent der Feldbreite). */
export const ROW_MIN_SPACING = 2;
export const ROW_MAX_SPACING = 50;
export const ROW_DEFAULT_SPACING = 10;

export type RowDirection = 'horizontal' | 'vertical' | 'diagonal' | 'target';

/** Elemente mit einem Punkt, aus denen sich eine Reihe erzeugen laesst (nicht Tor, Pfeil, Masslinie, Zone, Text). */
export type RowSource = Extract<DiagramElement, { type: 'player' | 'cone' | 'pole' | 'ring' | 'hurdle' | 'ball' }>;

export function isRowSource(element: DiagramElement | undefined): element is RowSource {
  return element !== undefined && element.type !== 'goal' && element.type !== 'arrow' && element.type !== 'zone' && element.type !== 'measure' && element.type !== 'text';
}

/** Richtungen, die der Reihe nach probiert werden, bis die Reihe ins Feld passt (Vorzeichen in x und y). */
const ROW_VARIANTS: Record<Exclude<RowDirection, 'target'>, ReadonlyArray<readonly [number, number]>> = {
  horizontal: [[1, 0], [-1, 0]],
  vertical: [[0, 1], [0, -1]],
  diagonal: [[1, 1], [-1, 1], [1, -1], [-1, -1]],
};

/**
 * Die Punkte einer Reihe von `count` Elementen, der erste ist `start`. Waagrecht, senkrecht und
 * diagonal haben je `spacing` Feldeinheiten Abstand (diagonal gemessen auf der Schraegen; senkrechte
 * Strecken in Zeichnungseinheiten wie sonst auch). Passt die Reihe in der ersten Richtung nicht ins
 * Feld, wird die Gegenrichtung (bei diagonal auch die anderen Ecken) versucht. Bei `target` liegen die
 * Elemente gleichmaessig zwischen `start` und `target`. Passt nichts oder fehlt der Zielpunkt: `null`.
 */
export function rowPoints(
  start: FieldPoint,
  count: number,
  direction: RowDirection,
  spacing: number,
  pitch: DiagramPitch,
  target: FieldPoint | null = null,
): FieldPoint[] | null {
  const n = Math.round(count);
  if (n < 2) return null;
  const inField = (points: FieldPoint[]): boolean => points.every((p) => p.x >= 0 && p.x <= 100 && p.y >= 0 && p.y <= 100);
  if (direction === 'target') {
    if (!target || (target.x === start.x && target.y === start.y)) return null;
    const points = Array.from({ length: n }, (_, i) => ({
      x: toCoordinate(start.x + ((target.x - start.x) * i) / (n - 1)),
      y: toCoordinate(start.y + ((target.y - start.y) * i) / (n - 1)),
    }));
    return inField(points) ? points : null;
  }
  const height = PITCH_HEIGHT[pitch];
  // Schritt in Feldprozent: y rechnet die Zeichnungseinheiten auf Prozent der Hoehe um.
  const along = direction === 'diagonal' ? spacing / Math.SQRT2 : spacing;
  const stepX = direction === 'vertical' ? 0 : along;
  const stepY = direction === 'horizontal' ? 0 : (along * 100) / height;
  for (const [sx, sy] of ROW_VARIANTS[direction]) {
    const points = Array.from({ length: n }, (_, i) => ({
      x: roundCoordinate(start.x + sx * stepX * i),
      y: roundCoordinate(start.y + sy * stepY * i),
    }));
    if (inField(points)) return points;
  }
  return null;
}

/**
 * Die neuen Elemente einer Reihe: Kopien des Ausgangselements an den Punkten ab dem zweiten
 * (der erste ist das Ausgangselement selbst). Spieler werden wie beim Duplizieren neu nummeriert.
 */
export function rowCopies(elements: readonly DiagramElement[], index: number, points: readonly FieldPoint[]): DiagramElement[] {
  const source = elements[index];
  if (!isRowSource(source)) return [];
  const copies: DiagramElement[] = [];
  for (const point of points.slice(1)) {
    copies.push(renumbered({ ...source, x: point.x, y: point.y }, [...elements, ...copies]));
  }
  return copies;
}

/** Eingaben des Bereichs "Reihe erzeugen"; Anzahl und Abstand als Text, wie sie im Feld stehen. */
export interface RowInput {
  count: string;
  spacing: string;
  direction: RowDirection;
  /** Zielpunkt bei "bis zu einem Punkt". */
  target: FieldPoint | null;
}

export interface RowPlan {
  /** Alle Punkte der Reihe samt Ausgangspunkt; `null`, wenn die Reihe nicht entstehen kann. */
  points: FieldPoint[] | null;
  /** Die neuen Elemente - leer, solange es ein Problem gibt. */
  copies: DiagramElement[];
  /** Deutscher Hinweis, warum die Reihe (noch) nicht moeglich ist. */
  problem: string | null;
}

function wholeNumber(text: string): number | null {
  return /^\d{1,3}$/.test(text.trim()) ? Number(text.trim()) : null;
}

/**
 * Plant eine Reihe aus dem Element `index`: prueft Anzahl, Abstand, Zielpunkt, die Lage im Feld und die
 * Hoechstzahl von 80 (`DIAGRAM_MAX_ELEMENTS`) Elementen und liefert die neuen Elemente - oder den Grund, warum es nicht geht.
 */
export function planRow(elements: readonly DiagramElement[], index: number, input: RowInput, pitch: DiagramPitch): RowPlan {
  const none = (problem: string): RowPlan => ({ points: null, copies: [], problem });
  const source = elements[index];
  if (!isRowSource(source)) return none('Aus diesem Element lässt sich keine Reihe erzeugen.');
  const count = wholeNumber(input.count);
  if (count === null || count < ROW_MIN_COUNT || count > ROW_MAX_COUNT) return none(`Anzahl: ${ROW_MIN_COUNT} bis ${ROW_MAX_COUNT}.`);
  const spacing = wholeNumber(input.spacing);
  if (input.direction !== 'target' && (spacing === null || spacing < ROW_MIN_SPACING || spacing > ROW_MAX_SPACING)) {
    return none(`Abstand: ${ROW_MIN_SPACING} bis ${ROW_MAX_SPACING} Feldeinheiten.`);
  }
  if (input.direction === 'target' && !input.target) return none('Tippe aufs Feld, wohin die Reihe reichen soll.');
  const points = rowPoints({ x: source.x, y: source.y }, count, input.direction, spacing ?? ROW_DEFAULT_SPACING, pitch, input.target);
  if (!points) {
    return none(input.direction === 'target' ? 'Der Zielpunkt liegt auf dem Ausgangselement – tippe einen anderen Punkt.' : 'Die Reihe passt nicht ins Feld – nimm weniger Elemente oder einen kleineren Abstand.');
  }
  if (elements.length + count - 1 > DIAGRAM_MAX_ELEMENTS) {
    return none(`Mit ${count - 1} neuen Elementen hätte die Skizze ${elements.length + count - 1} – erlaubt sind höchstens ${DIAGRAM_MAX_ELEMENTS}.`);
  }
  return { points, copies: rowCopies(elements, index, points), problem: null };
}
